import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Loader2, Sparkles, Upload, Globe, Plus, Trash2, TrendingUp, FileCode2,
  Send, ShoppingBag, HandCoins, FolderOpen, Info as InfoIcon, ShoppingCart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import PropertyAdCreator from "@/components/property-admin/PropertyAdCreator";
import { EstimatePriceModal } from "@/components/property-admin/EstimatePriceModal";
import { PlanningApplicationsModal } from "@/components/property-admin/PlanningApplicationsModal";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import { GalleryEditor } from "@/components/admin/GalleryEditor";
import { FeatureChipsInput } from "@/components/FeatureChipsInput";
import { ImageUpload } from "@/components/ImageUpload";
import { resolveImageUrl } from "@/utils/imageUrl";
import { AdvertisingSection } from "@/components/property-admin/AdvertisingSection";

const API = getApiUrl();

// Property-edit-modal section order, same mechanism the Master Admin's
// "CMS → Property Form" tab controls (`/public/property-form-layout`).
// Agents only see a subset of the sections that exist there (no Agent,
// SEO, Import URL block or Rightmove-only groups — those are admin-only),
// so each field is mapped into whichever of the shared sections it
// belongs to; fields within a section keep their existing fixed order.
const DEFAULT_SECTION_ORDER = [
  "title", "listing_status", "page_display", "import_url", "price", "address",
  "general_details", "video", "gallery", "floorplan", "agent", "vendor", "seo",
  "rm_type_status", "rm_address", "rm_dates", "rm_price_tenure",
  "rm_description", "rm_rooms_size", "rm_amenities", "rm_regulatory", "rm_media",
];
const AGENT_SECTION_FIELDS = {
  title: ["title"],
  listing_status: ["is_live"],
  price: ["price"],
  address: ["display_address", "street_name", "cms_town_city", "postcode", "cms_House_Name_Number", "latitude", "longitude"],
  general_details: ["beds", "baths", "size", "property_type", "tenure", "nearest_stations", "council_tax_band", "parking_spaces", "garden_facing", "date_added", "features", "description"],
  video: ["video_url"],
  gallery: ["gallery"],
  floorplan: ["floorplan_image"],
};
const AGENT_SECTION_IDS = Object.keys(AGENT_SECTION_FIELDS);

/**
 * The "details" tab — the full property edit form (Dialog). Extracted from
 * PropertyAdminDashboard.jsx (pure move, no behavior change) to keep that
 * file's size manageable.
 *
 * Fields render grouped into named blocks (Title, Listing Status, Price,
 * Address, General Details, Video, Gallery, Floorplan) ordered the same
 * way as the Master Admin's "CMS → Property Form" layout, so dragging a
 * section there reorders this form too.
 */
export function PropertyEditDialog({
  activeTab,
  setActiveTab,
  propertyData,
  setPropertyData,
  handleChange,
  handleNumberChange,
  vis,
  setHasUnsavedChanges,
  setShowNewPropertyDialog,
  setShowScrapeDialog,
  loginType,
  aiRewriting,
  handleAIRewrite,
  handleGalleryUpload,
  handleDeleteProperty,
  deletingProperty,
  onOpenEnquiries,
  onOpenOrders,
  onOpenOffersReceived,
  onOpenDocuments,
  onOpenInfo,
  onOpenBuyProducts,
}) {
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER);
  const [showEstimateModal, setShowEstimateModal] = useState(false);
  const [showPlanningAppsModal, setShowPlanningAppsModal] = useState(false);
  const { styles: siteStylesData } = useSiteStyles();
  const estimatePriceCtaEnabled = siteStylesData?.estimatePriceCtaEnabled !== false;
  const planningAppsCtaEnabled = siteStylesData?.planningAppsCtaEnabled !== false;
  const ctaSocialAdEnabled = siteStylesData?.ctaSocialAdEnabled !== false;
  const ctaImportUrlEnabled = siteStylesData?.ctaImportUrlEnabled !== false;
  const ctaRendyEnabled = siteStylesData?.ctaRendyEnabled !== false;

  // Custom admin-set label (CMS → Fields "Label" input) overrides the
  // built-in default label wherever a field is shown, here included.
  const fieldLabel = (key, fallback) => vis.settings[key]?.label || fallback;

  // Same postcode-split / title-build logic as the public "List your
  // property" capture form (CapturePropertyFormModule.jsx) — kept
  // identical so admin edits stay consistent with what Rightmove sends.

  // UK postcode splits into an "outward" (area+district, e.g. "SW1A") and
  // "inward" (sector+unit, e.g. "1AA") code — usually separated by a space.
  const splitPostcode = (raw) => {
    const trimmed = (raw || "").trim().toUpperCase();
    if (!trimmed) return { first: "", last: "" };
    const parts = trimmed.split(/\s+/);
    if (parts.length >= 2) return { first: parts[0], last: parts[parts.length - 1] };
    if (trimmed.length > 3) return { first: trimmed.slice(0, -3), last: trimmed.slice(-3) };
    return { first: trimmed, last: "" };
  };

  // Title auto-builds from "<beds> <property_type> in <cms_town_city>"
  // whenever any of those three change — always recomputed (overwrites
  // any manual edit), mirroring the capture form exactly.
  const TITLE_TRIGGER_KEYS = ["beds", "property_type", "cms_town_city"];
  const buildAutoTitle = (v) => {
    const bedsPart = v.beds ? `${v.beds} Bedroom` : "";
    const typePart = (v.property_type || "").trim();
    const town = (v.cms_town_city || "").trim();
    let title = [bedsPart, typePart].filter(Boolean).join(" ");
    if (town) title = title ? `${title} in ${town}` : `In ${town}`;
    return title;
  };

  // street_name + cms_town_city auto-build Display Address ("<street>,
  // <town>") once BOTH are filled in — left untouched while either is
  // still empty, so it never clobbers a manual edit made before that.
  // Also drives the auto title above whenever beds/property_type/town
  // change (whichever field triggered this handler).
  const handleAddressPartChange = (e) => {
    const { name, value } = e.target;
    setPropertyData((prev) => {
      const parsedValue = name === "beds" ? (parseInt(value) || 0) : value;
      const next = { ...prev, [name]: parsedValue };
      const street = (next.street_name || "").trim();
      const town = (next.cms_town_city || "").trim();
      if (street && town) next.display_address = `${street}, ${town}`;
      if (TITLE_TRIGGER_KEYS.includes(name)) next.title = buildAutoTitle(next);
      return next;
    });
    setHasUnsavedChanges(true);
  };

  // Postcode field auto-populates cms_postcode_1 (outward) / cms_postcode_2
  // (inward) — same fields the Rightmove feed and capture form use.
  const handlePostcodeChange = (e) => {
    const value = e.target.value;
    const { first, last } = splitPostcode(value);
    setPropertyData((prev) => ({ ...prev, postcode: value, cms_postcode_1: first, cms_postcode_2: last }));
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/public/property-form-layout`)
      .then((r) => {
        if (!cancelled && Array.isArray(r.data?.order) && r.data.order.length) {
          setSectionOrder(r.data.order);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Each entry: key -> { width: "half" | "full", node }. `width` controls
  // whether it takes one or both columns of the shared 2-col field grid
  // below (matches the original layout's use of `col-span-2`).
  const fieldRenderers = propertyData ? {
    title: {
      width: "full",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("title", "Title")} *</Label>
          <Input
            name="title"
            value={propertyData.title || ""}
            onChange={handleChange}
            placeholder="Property title"
            className="rounded-none"
            data-testid="property-title-editor"
          />
        </div>
      ),
    },
    is_live: {
      width: "full",
      node: (
        <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${(propertyData.is_live === true || propertyData.is_live === undefined) ? 'bg-green-500' : 'bg-red-500'}`} />
              <div>
                <h4 className="font-medium">{fieldLabel("is_live", "Listing Status")}</h4>
                <p className="text-sm text-muted-foreground">
                  {(propertyData.is_live === true || propertyData.is_live === undefined) ? 'Property is visible on the website' : 'Property is hidden from the website'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Live</span>
              <button
                type="button"
                role="switch"
                aria-checked={propertyData.is_live === true || propertyData.is_live === undefined}
                onClick={() => {
                  const currentlyLive = propertyData.is_live === true || propertyData.is_live === undefined;
                  setPropertyData(prev => ({ ...prev, is_live: !currentlyLive }));
                  setHasUnsavedChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  (propertyData.is_live === true || propertyData.is_live === undefined) ? 'bg-green-500' : 'bg-gray-300'
                }`}
                data-testid="live-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    (propertyData.is_live === true || propertyData.is_live === undefined) ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            {vis.shouldShow("live_from", "user") && (
            <div className="space-y-2">
              <Label htmlFor="live_from">{fieldLabel("live_from", "Live From")}</Label>
              <Input
                id="live_from"
                name="live_from"
                type="date"
                value={propertyData.live_from || new Date().toISOString().split('T')[0]}
                onChange={handleChange}
                className="rounded-none"
                data-testid="live-from-date"
              />
            </div>
            )}
            {vis.shouldShow("live_to", "user") && (
            <div className="space-y-2">
              <Label htmlFor="live_to">{fieldLabel("live_to", "Live Until")}</Label>
              <Input
                id="live_to"
                name="live_to"
                type="date"
                value={propertyData.live_to || (() => {
                  const d = new Date();
                  d.setMonth(d.getMonth() + 12);
                  return d.toISOString().split('T')[0];
                })()}
                onChange={handleChange}
                className="rounded-none"
                data-testid="live-to-date"
              />
              <p className="text-xs text-muted-foreground">Default: 12 months from today</p>
            </div>
            )}
          </div>
        </div>
      ),
    },
    price: {
      width: "full",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("price", "Price")} *</Label>
          <div className="flex gap-2">
            <select
              name="currency"
              value={propertyData.currency || "£"}
              onChange={handleChange}
              className="h-10 px-2 border border-input bg-background text-sm rounded-none w-16 shrink-0"
            >
              <option value="£">£</option>
              <option value="$">$</option>
              <option value="€">€</option>
            </select>
            <Input
              name="price"
              type="number"
              value={propertyData.price || ""}
              onChange={handleNumberChange}
              placeholder="950000"
              className="rounded-none flex-1"
              data-testid="prop-admin-price"
            />
          </div>
        </div>
      ),
    },
    display_address: {
      width: "full",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("display_address", "Display Address")} *</Label>
          <Input
            name="display_address"
            value={propertyData.display_address || ""}
            onChange={handleChange}
            className="rounded-none"
            data-testid="prop-admin-location"
          />
        </div>
      ),
    },
    street_name: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("street_name", "Street Name")}</Label>
          <Input
            name="street_name"
            value={propertyData.street_name || ""}
            onChange={handleAddressPartChange}
            className="rounded-none"
            data-testid="prop-admin-street-name"
          />
        </div>
      ),
    },
    cms_town_city: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("cms_town_city", "Town / City")}</Label>
          <Input
            name="cms_town_city"
            value={propertyData.cms_town_city || ""}
            onChange={handleAddressPartChange}
            className="rounded-none"
            data-testid="prop-admin-town-city"
          />
        </div>
      ),
    },
    postcode: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("postcode", "Postcode")}</Label>
          <Input
            name="postcode"
            value={propertyData.postcode || ""}
            onChange={handlePostcodeChange}
            placeholder="e.g. SW1A 1AA"
            className="rounded-none"
            data-testid="prop-admin-postcode"
          />
          <p className="text-xs text-muted-foreground">
            Auto-splits into cms_postcode_1 / cms_postcode_2 for the Rightmove feed.
          </p>
        </div>
      ),
    },
    cms_House_Name_Number: {
      width: "full",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("cms_House_Name_Number", "Address 1 (for Google Maps)")}</Label>
          <Input
            name="cms_House_Name_Number"
            value={propertyData.cms_House_Name_Number || ""}
            onChange={handleChange}
            placeholder="123 Main St, City, State ZIP"
            className="rounded-none"
            data-testid="prop-admin-address"
          />
        </div>
      ),
    },
    beds: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("beds", "Bedrooms")}</Label>
          <Input
            name="beds"
            type="number"
            value={propertyData.beds || ""}
            onChange={handleAddressPartChange}
            className="rounded-none"
            data-testid="prop-admin-beds"
          />
        </div>
      ),
    },
    baths: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("baths", "Bathrooms")}</Label>
          <Input
            name="baths"
            type="number"
            value={propertyData.baths || ""}
            onChange={handleNumberChange}
            className="rounded-none"
            data-testid="prop-admin-baths"
          />
        </div>
      ),
    },
    size: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("size", "Size")}</Label>
          <Input
            name="size"
            value={propertyData.size || ""}
            onChange={handleChange}
            placeholder="2,500 sq ft"
            className="rounded-none"
            data-testid="prop-admin-size"
          />
        </div>
      ),
    },
    property_type: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("property_type", "Home Type")}</Label>
          <select
            name="property_type"
            value={propertyData.property_type || ""}
            onChange={handleAddressPartChange}
            className="w-full h-10 px-3 border border-input bg-background rounded-none text-sm"
            data-testid="prop-admin-type"
          >
            <option value="">Select type...</option>
            <option value="terraced">Terraced</option>
            <option value="semi-detached">Semi-Detached</option>
            <option value="detached">Detached</option>
            <option value="townhouse">Townhouse</option>
            <option value="flat">Flat</option>
            <option value="bungalow">Bungalow</option>
            <option value="land">Land</option>
          </select>
        </div>
      ),
    },
    tenure: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("tenure", "Tenure")}</Label>
          <select
            name="tenure"
            value={propertyData.tenure || ""}
            onChange={handleChange}
            className="w-full h-10 px-3 border border-input bg-background rounded-none text-sm"
            data-testid="prop-admin-tenure"
          >
            <option value="">Select tenure...</option>
            <option value="freehold">Freehold</option>
            <option value="leasehold">Leasehold</option>
            <option value="new-lease">New Lease</option>
            <option value="long-lease">Long Lease</option>
          </select>
        </div>
      ),
    },
    nearest_stations: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("nearest_stations", "Nearest Stations")}</Label>
          <Input
            name="nearest_stations"
            value={propertyData.nearest_stations || ""}
            onChange={handleChange}
            placeholder="Victoria Line - 0.5 miles"
            className="rounded-none"
            data-testid="prop-admin-stations"
          />
        </div>
      ),
    },
    council_tax_band: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("council_tax_band", "Council Tax Band")}</Label>
          <select
            name="council_tax_band"
            value={propertyData.council_tax_band || ""}
            onChange={handleChange}
            className="w-full h-10 px-3 border border-input bg-background rounded-none text-sm"
            data-testid="prop-admin-council-tax"
          >
            <option value="">Select band...</option>
            <option value="A">Band A</option>
            <option value="B">Band B</option>
            <option value="C">Band C</option>
            <option value="D">Band D</option>
            <option value="E">Band E</option>
            <option value="F">Band F</option>
            <option value="G">Band G</option>
            <option value="H">Band H</option>
          </select>
        </div>
      ),
    },
    parking_spaces: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("parking_spaces", "Parking Spaces")}</Label>
          <select
            name="parking_spaces"
            value={propertyData.parking_spaces || ""}
            onChange={handleChange}
            className="w-full h-10 px-3 border border-input bg-background rounded-none text-sm"
            data-testid="prop-admin-parking"
          >
            <option value="">Select...</option>
            {[...Array(21)].map((_, i) => (
              <option key={i} value={i}>{i} {i === 1 ? 'space' : 'spaces'}</option>
            ))}
          </select>
        </div>
      ),
    },
    garden_facing: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("garden_facing", "Garden Facing")}</Label>
          <select
            name="garden_facing"
            value={propertyData.garden_facing || ""}
            onChange={handleChange}
            className="w-full h-10 px-3 border border-input bg-background rounded-none text-sm"
            data-testid="prop-admin-garden"
          >
            <option value="">Select direction...</option>
            <option value="No Garden">No Garden</option>
            <option value="N">North</option>
            <option value="NE">North East</option>
            <option value="E">East</option>
            <option value="SE">South East</option>
            <option value="S">South</option>
            <option value="SW">South West</option>
            <option value="W">West</option>
            <option value="NW">North West</option>
          </select>
        </div>
      ),
    },
    date_added: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("date_added", "Date Added")}</Label>
          <Input
            name="date_added"
            type="date"
            value={propertyData.date_added || ""}
            onChange={handleChange}
            className="rounded-none"
            data-testid="prop-admin-date"
          />
        </div>
      ),
    },
    features: {
      width: "full",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("features", "Features")}</Label>
          <FeatureChipsInput
            value={propertyData.features || ""}
            onChange={(next) => {
              setPropertyData((prev) => ({ ...prev, features: next }));
              setHasUnsavedChanges(true);
            }}
          />
        </div>
      ),
    },
    description: {
      width: "full",
      node: (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>{fieldLabel("description", "Description")}</Label>
            <Button
              type="button"
              onClick={handleAIRewrite}
              disabled={aiRewriting || !propertyData.description}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold px-4 py-1.5 rounded-full shadow-md hover:shadow-lg transition-all duration-200 gap-2 text-sm"
              data-testid="ai-rewrite-btn"
            >
              {aiRewriting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Rewriting...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  AI
                </>
              )}
            </Button>
          </div>
          <Textarea
            name="description"
            value={propertyData.description || ""}
            onChange={handleChange}
            className="rounded-none min-h-32"
            placeholder="Enter property description or click AI to enhance it"
            data-testid="prop-admin-description"
          />
        </div>
      ),
    },
    video_url: {
      width: "full",
      node: (
        <>
          <div className="space-y-4">
            <Label className="font-medium">{fieldLabel("video_url", "Video Tour")}</Label>

            {/* CTA for Video Upload */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg space-y-3">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <Upload className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900">Upload Your Home Video Tour</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Showcase your property with a professional video tour. Upload videos up to 50MB directly or embed from YouTube, Instagram, or TikTok.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    document.querySelector('[data-testid="property-images-section"]')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                >
                  <Upload size={18} />
                  Upload Video (Max 50MB)
                </button>
                <span className="text-sm text-gray-500 self-center">or embed from social media below</span>
              </div>
            </div>

            {/* Video Type Selector */}
            <div className="flex items-center gap-4 p-3 bg-muted/30 border border-border">
              <span className="text-sm text-muted-foreground">Platform:</span>
              <div className="flex gap-2">
                {[
                  { value: "youtube", label: "YouTube", icon: "📺" },
                  { value: "instagram", label: "Instagram", icon: "📷" },
                  { value: "tiktok", label: "TikTok", icon: "🎵" }
                ].map((platform) => (
                  <button
                    key={platform.value}
                    type="button"
                    onClick={() => {
                      setPropertyData(prev => ({ ...prev, video_type: platform.value }));
                      setHasUnsavedChanges(true);
                    }}
                    className={`px-4 py-2 text-sm rounded-none border transition-colors ${
                      (propertyData.video_type || "youtube") === platform.value
                        ? "bg-primary text-white border-primary"
                        : "bg-white border-border hover:border-primary"
                    }`}
                  >
                    {platform.icon} {platform.label}
                  </button>
                ))}
              </div>
            </div>

            <Input
              name="video_url"
              value={propertyData.video_url || ""}
              onChange={handleChange}
              placeholder={
                (propertyData.video_type || "youtube") === "youtube"
                  ? "https://www.youtube.com/watch?v=..."
                  : (propertyData.video_type || "youtube") === "instagram"
                    ? "https://www.instagram.com/p/... or /reel/..."
                    : "https://www.tiktok.com/@user/video/..."
              }
              className="rounded-none"
              data-testid="prop-admin-video"
            />
          </div>

          {propertyData.video_url && (
            <div className="flex items-center gap-3 mt-4">
              <Switch
                checked={propertyData.show_video_thumbnail !== false}
                onCheckedChange={(checked) => {
                  setPropertyData(prev => ({ ...prev, show_video_thumbnail: checked }));
                  setHasUnsavedChanges(true);
                }}
                data-testid="prop-admin-show-video"
              />
              <Label>Show video thumbnail in home listings</Label>
            </div>
          )}
        </>
      ),
    },
    gallery: {
      width: "full",
      node: (
        <div className="space-y-4" data-testid="property-images-section">
          <Label>{fieldLabel("gallery", "Home Images")} ({propertyData.gallery?.length || 0})</Label>
          <p className="text-xs text-muted-foreground">
            Use the uploader to add new files. Edit fields inline or drag rows to reorder. First (sort_order 0) = Featured.
          </p>
          <ImageUpload
            onUpload={handleGalleryUpload}
            multiple={true}
            currentImages={[]}
            label="Upload Images"
            skipWatermark={false}
          />
          <GalleryEditor
            value={propertyData.gallery || []}
            onChange={(next) => {
              setPropertyData((prev) => ({ ...prev, gallery: next }));
              setHasUnsavedChanges(true);
            }}
            propertyId={propertyData.property_id}
            showRendyPanel={ctaRendyEnabled}
          />
          {propertyData.gallery && propertyData.gallery.length > 0 && (
            <div className="p-3 bg-muted/30 border border-border">
              <p className="text-xs font-medium text-muted-foreground mb-2">Featured Image Preview:</p>
              <img
                src={resolveImageUrl((propertyData.gallery[0] && propertyData.gallery[0].media_url) || "")}
                alt="Featured"
                className="w-full max-w-md h-48 object-cover border border-primary"
              />
            </div>
          )}
        </div>
      ),
    },
    floorplan_image: {
      width: "full",
      node: (
        <div className="space-y-4">
          <Label>{fieldLabel("floorplan_image", "Floorplan")}</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              {propertyData.floorplan_image && (
                <div className="relative w-full">
                  <img
                    src={resolveImageUrl(propertyData.floorplan_image)}
                    alt="Floorplan"
                    className="w-full h-48 object-contain border border-border bg-muted/30"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPropertyData(prev => ({ ...prev, floorplan_image: "" }));
                      setHasUnsavedChanges(true);
                    }}
                    className="absolute top-2 right-2 bg-destructive text-white p-1 rounded text-xs hover:bg-destructive/90"
                    data-testid="remove-floorplan-btn"
                  >
                    Remove
                  </button>
                </div>
              )}
              <ImageUpload
                onUpload={(url) => {
                  const floorplanUrl = Array.isArray(url) ? url[0] : url;
                  setPropertyData(prev => ({ ...prev, floorplan_image: floorplanUrl }));
                  setHasUnsavedChanges(true);
                  toast.success("Floorplan uploaded successfully");
                }}
                multiple={false}
                label="Upload Floorplan"
                skipWatermark={true}
                testId="floorplan-upload"
              />
              <div className="text-xs text-muted-foreground">Or enter URL:</div>
              <Input
                name="floorplan_image"
                value={propertyData.floorplan_image || ""}
                onChange={handleChange}
                placeholder="https://... or /api/uploads/..."
                className="rounded-none"
                data-testid="floorplan-url-input"
              />
            </div>
            {vis.shouldShow("floorplan_size", "user") && (
            <div className="space-y-2">
              <Label>{fieldLabel("floorplan_size", "Floorplan Size")}</Label>
              <Input
                name="floorplan_size"
                value={propertyData.floorplan_size || ""}
                onChange={handleChange}
                placeholder="e.g., 2,500 sq ft"
                className="rounded-none"
                data-testid="floorplan-size-input"
              />
              <p className="text-xs text-muted-foreground">
                Size to display next to the floorplan
              </p>
            </div>
            )}
          </div>
        </div>
      ),
    },
    latitude: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("latitude", "Latitude")} (optional)</Label>
          <Input
            name="latitude"
            type="number"
            step="any"
            value={propertyData.latitude || ""}
            onChange={handleChange}
            placeholder="34.0736"
            className="rounded-none"
          />
        </div>
      ),
    },
    longitude: {
      width: "half",
      node: (
        <div className="space-y-2">
          <Label>{fieldLabel("longitude", "Longitude")} (optional)</Label>
          <Input
            name="longitude"
            type="number"
            step="any"
            value={propertyData.longitude || ""}
            onChange={handleChange}
            placeholder="-118.4004"
            className="rounded-none"
          />
        </div>
      ),
    },
  } : {};

  // CSS-order lookup — mirrors Master Admin's `sectionOrderIdx` so the
  // same "CMS → Property Form" order drives both dialogs.
  const sectionOrderIdx = (id) => {
    const i = sectionOrder.indexOf(id);
    return i === -1 ? 999 : i;
  };

  // Bucket every field above into its shared section, dropping any field
  // hidden via CMS Fields visibility and any section left with no visible
  // fields, then sort by `sectionOrder` so DOM/tab order matches the
  // visual order (not just the CSS `order` property).
  const orderedSections = useMemo(() => {
    return AGENT_SECTION_IDS
      .map((id) => ({
        id,
        keys: AGENT_SECTION_FIELDS[id].filter((k) => fieldRenderers[k] && vis.shouldShow(k, "user")),
      }))
      .filter((s) => s.keys.length > 0)
      .sort((a, b) => sectionOrderIdx(a.id) - sectionOrderIdx(b.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyData, vis.settings, sectionOrder]);

  return (
              <div className="p-6" data-testid="property-edit-dialog">
            {/* Guard: Ensure propertyData is fully loaded before rendering details */}
            {!propertyData || !propertyData.property_id ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
            {/* Edit Form */}
            <div className="bg-white border border-border p-6 space-y-8">
              <div className="flex items-center justify-between border-b border-border pb-4 gap-3 flex-wrap">
                <h3 className="font-serif text-xl font-medium flex items-baseline gap-2" data-testid="edit-home-details-heading">
                  Edit Home Details
                  {propertyData.title && (
                    <span className="font-sans text-sm font-normal text-muted-foreground truncate max-w-xs" data-testid="edit-home-details-property-title">
                      — {propertyData.title}
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  {loginType === "agent" && ctaSocialAdEnabled && (
                    <PropertyAdCreator 
                      propertyId={propertyData.property_id}
                      propertyData={propertyData}
                    />
                  )}
                  {siteStylesData?.ctaOffersEnabled !== false && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs"
                      onClick={onOpenOffersReceived}
                      data-testid="edit-portal-offers-received-btn"
                    >
                      <HandCoins className="mr-1 h-3 w-3" />
                      Offers Received
                    </Button>
                  )}
                  {siteStylesData?.ctaEnquiriesEnabled !== false && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs"
                      onClick={onOpenEnquiries}
                      data-testid="edit-portal-enquiries-btn"
                    >
                      <Send className="mr-1 h-3 w-3" />
                      Enquiries
                    </Button>
                  )}
                  {siteStylesData?.ctaOrdersEnabled !== false && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs"
                      onClick={onOpenOrders}
                      data-testid="edit-portal-orders-btn"
                    >
                      <ShoppingBag className="mr-1 h-3 w-3" />
                      Orders
                    </Button>
                  )}
                  {siteStylesData?.ctaDocumentsEnabled !== false && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs"
                      onClick={onOpenDocuments}
                      data-testid="edit-portal-documents-btn"
                    >
                      <FolderOpen className="mr-1 h-3 w-3" />
                      Documents
                    </Button>
                  )}
                  {siteStylesData?.ctaInfoEnabled !== false && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs"
                      onClick={onOpenInfo}
                      data-testid="edit-portal-info-btn"
                    >
                      <InfoIcon className="mr-1 h-3 w-3" />
                      Info
                    </Button>
                  )}
                  {siteStylesData?.ctaBuyingEnabled !== false && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs"
                      onClick={onOpenBuyProducts}
                      data-testid="edit-portal-buy-products-btn"
                    >
                      <ShoppingCart className="mr-1 h-3 w-3" />
                      Buy Products
                    </Button>
                  )}
                  {propertyData.property_id && estimatePriceCtaEnabled && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs"
                      onClick={() => setShowEstimateModal(true)}
                      data-testid="estimate-price-btn"
                    >
                      <TrendingUp className="mr-1 h-3 w-3" />
                      Estimate Price
                    </Button>
                  )}
                  {propertyData.property_id && planningAppsCtaEnabled && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs"
                      onClick={() => setShowPlanningAppsModal(true)}
                      data-testid="planning-applications-btn"
                    >
                      <FileCode2 className="mr-1 h-3 w-3" />
                      Planning Applications
                    </Button>
                  )}
                  {ctaImportUrlEnabled && (
                    <Button
                      onClick={() => setShowScrapeDialog(true)}
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs"
                      data-testid="import-url-btn"
                    >
                      <Globe className="mr-1 h-3 w-3" />
                      Import URL
                    </Button>
                  )}
                </div>
              </div>

              <EstimatePriceModal
                propertyId={propertyData.property_id}
                open={showEstimateModal}
                onOpenChange={setShowEstimateModal}
                onUsePrice={(value) => {
                  toast.success(`Suggested price noted: £${value.toLocaleString("en-GB")} — share this with your Admin`);
                }}
              />

              <PlanningApplicationsModal
                propertyId={propertyData.property_id}
                open={showPlanningAppsModal}
                onOpenChange={setShowPlanningAppsModal}
              />

              {/* Fields render grouped into sections, ordered per CMS → Property Form Layout */}
              <div className="flex flex-col gap-6" data-testid="property-edit-fields-grid">
                {orderedSections.map(({ id, keys }) => (
                  <div
                    key={id}
                    className="grid grid-cols-2 gap-4"
                    style={{ order: sectionOrderIdx(id) }}
                    data-testid={`property-edit-section-${id}`}
                  >
                    {keys.map((key) => {
                      const meta = vis.settings[key] || {};
                      const val = propertyData[key];
                      const isBlank = val === undefined || val === null || val === ""
                        || (typeof val === "number" && Number.isNaN(val));
                      const missing = !!meta.required && isBlank;
                      return (
                        <div
                          key={key}
                          className={`${fieldRenderers[key].width === "full" ? "col-span-2" : ""} ${missing ? "ring-1 ring-red-400 bg-red-50/60 rounded p-2 -m-2" : ""}`}
                          data-testid={`property-edit-field-${key}`}
                        >
                          {fieldRenderers[key].node}
                          {missing && (
                            <p className="text-[11px] text-red-600 mt-1" data-testid={`property-edit-field-required-warning-${key}`}>
                              Required — please fill this in
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Vendor Details — the property owner's contact info. Defaults
                  from whoever submitted "List your property" but is fully
                  editable, since the submitter isn't always the vendor. */}
              <div className="space-y-4 pt-4 border-t border-border" data-testid="property-vendor-details-section">
                <Label className="text-base font-medium">Vendor Details</Label>
                <p className="text-sm text-muted-foreground">
                  Contact details for the property owner
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vendor Name</Label>
                    <Input
                      name="vendor_name"
                      value={propertyData.vendor_name || ""}
                      onChange={handleChange}
                      className="rounded-none"
                      data-testid="prop-admin-vendor-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Vendor Phone</Label>
                    <Input
                      name="vendor_phone"
                      value={propertyData.vendor_phone || ""}
                      onChange={handleChange}
                      className="rounded-none"
                      data-testid="prop-admin-vendor-phone"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Vendor Email</Label>
                    <Input
                      type="email"
                      name="vendor_email"
                      value={propertyData.vendor_email || ""}
                      onChange={handleChange}
                      className="rounded-none"
                      data-testid="prop-admin-vendor-email"
                    />
                  </div>
                </div>
              </div>

              {/* Page Display Options — not a CMS field, always shown last */}
              <div className="space-y-4 pt-4 border-t border-border">
                <Label className="text-base font-medium">Page Display Options</Label>
                <p className="text-sm text-muted-foreground">
                  Control what is shown on your property landing page
                </p>

                <div className="space-y-4 p-4 bg-muted/30 border border-border rounded-sm">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Show Website Header</Label>
                      <p className="text-xs text-muted-foreground">
                        Display the SellLocalHomes navigation bar at the top
                      </p>
                    </div>
                    <Switch
                      checked={propertyData.show_header !== false}
                      onCheckedChange={(checked) => {
                        setPropertyData(prev => ({ ...prev, show_header: checked }));
                        setHasUnsavedChanges(true);
                      }}
                      data-testid="prop-admin-show-header"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Show Website Footer</Label>
                      <p className="text-xs text-muted-foreground">
                        Display the SellLocalHomes footer at the bottom
                      </p>
                    </div>
                    <Switch
                      checked={propertyData.show_footer !== false}
                      onCheckedChange={(checked) => {
                        setPropertyData(prev => ({ ...prev, show_footer: checked }));
                        setHasUnsavedChanges(true);
                      }}
                      data-testid="prop-admin-show-footer"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Show Back to Homes Link</Label>
                      <p className="text-xs text-muted-foreground">
                        Display the link to return to the listings page
                      </p>
                    </div>
                    <Switch
                      checked={propertyData.show_back_link !== false}
                      onCheckedChange={(checked) => {
                        setPropertyData(prev => ({ ...prev, show_back_link: checked }));
                        setHasUnsavedChanges(true);
                      }}
                      data-testid="prop-admin-show-back-link"
                    />
                  </div>
                </div>
              </div>

              {(loginType === "agent" || loginType === "property_admin") && (
                <AdvertisingSection propertyId={propertyData.property_id} />
              )}

              {/* Danger Zone — self-service delete, always last on the page */}
              <div className="space-y-4 pt-4 border-t border-border">
                <Label className="text-base font-medium text-destructive">Danger Zone</Label>
                <div className="flex items-center justify-between gap-4 p-4 border border-destructive/30 bg-destructive/5 rounded-sm">
                  <div>
                    <p className="text-sm font-medium">Delete this home</p>
                    <p className="text-xs text-muted-foreground">
                      Permanently removes this listing, its enquiries and stats. This cannot be undone.
                    </p>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="rounded-none text-xs shrink-0"
                        disabled={deletingProperty}
                        data-testid="delete-property-btn"
                      >
                        {deletingProperty ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="mr-1 h-3 w-3" />
                        )}
                        Delete Property
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="delete-property-confirm-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{propertyData.title || "this home"}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This permanently deletes the listing along with its enquiries and statistics.
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-property-cancel-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() => handleDeleteProperty(propertyData.property_id)}
                          data-testid="delete-property-confirm-btn"
                        >
                          Delete Property
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
              </>
            )}
              </div>

  );
}

export default PropertyEditDialog;
